import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, BarChart2, Users, Clock, Receipt,
  TrendingUp, List, DollarSign, Download, Printer,
  RefreshCw, Search, ChevronUp, ChevronDown,
  AlertCircle, GitMerge, BookOpen, PieChart, Layers
} from 'lucide-react';
import apiClient from '../services/api';

const VIOLET = '#667eea';
const VIOLET_DK = '#764ba2';

const REPORT_GROUPS = [
  { label:'📋 Receivables', types:[
    { key:'outstanding',           label:'Outstanding Receivables', icon:AlertCircle },
    { key:'aged_receivables',       label:'Aged Receivables',        icon:Clock       },
    { key:'statement',             label:'Customer Statement',      icon:BookOpen    },
  ]},
  { label:'💰 Receipts', types:[
    { key:'receipt_history',        label:'Receipt History',         icon:Receipt     },
    { key:'receipt_summary_new',    label:'Receipt Summary',         icon:PieChart    },
    { key:'allocation_details',     label:'Allocation Details',      icon:GitMerge    },
  ]},
  { label:'🔗 Reconciliation', types:[
    { key:'reconciliation',         label:'Invoice Reconciliation',  icon:GitMerge    },
    { key:'invoice_receipt_summary',label:'Inv vs Receipt Summary',  icon:Layers      },
  ]},
  { label:'🧾 Invoices', types:[
    { key:'invoice_details',        label:'Invoice Details',         icon:Receipt     },
    { key:'invoice_summary',        label:'Invoice Summary',         icon:TrendingUp  },
    { key:'ledger_details',         label:'Ledger Details',          icon:FileText    },
    { key:'ledger_summary',         label:'Ledger Summary',          icon:BarChart2   },
    { key:'aging',                  label:'Aging Report',            icon:Clock       },
  ]},
  { label:'👥 Customers', types:[
    { key:'customer_list',          label:'Customer List',           icon:Users       },
  ]},
];

const REPORT_TYPES = REPORT_GROUPS.flatMap(g => g.types);

const COLUMNS = {
  outstanding:[
    {key:'CustomerCode',      label:'Code',         width:90 },
    {key:'CustomerName',      label:'Customer',     width:170},
    {key:'InvoiceNo',         label:'Invoice No',   width:120},
    {key:'InvoiceDate',       label:'Invoice Date', width:100,fmt:'date'},
    {key:'DueDate',           label:'Due Date',     width:100,fmt:'date'},
    {key:'InvoiceAmount',     label:'Invoice Amt',  width:120,align:'right',fmt:'num2'},
    {key:'PaidAmount',        label:'Paid',         width:110,align:'right',fmt:'num2green'},
    {key:'OutstandingAmount', label:'Outstanding',  width:125,align:'right',fmt:'balance'},
    {key:'DaysOverdue',       label:'Days Overdue', width:95, align:'right',fmt:'overdue'},
    {key:'AgingBucket',       label:'Bucket',       width:100,fmt:'aging'},
    {key:'Status',            label:'Status',       width:100,fmt:'invstatus'},
    {key:'PaymentTerms',      label:'Terms',        width:80 },
  ],
  aged_receivables:[
    {key:'CustomerCode',      label:'Code',         width:90 },
    {key:'CustomerName',      label:'Customer',     width:190},
    {key:'OpenInvoices',      label:'Open Inv',     width:80, align:'right'},
    {key:'TotalOutstanding',  label:'Total Due',    width:130,align:'right',fmt:'balance'},
    {key:'Current_0_30',      label:'Current',      width:110,align:'right',fmt:'num2'},
    {key:'Days_1_30',         label:'1-30 Days',    width:110,align:'right',fmt:'num2'},
    {key:'Days_31_60',        label:'31-60 Days',   width:110,align:'right',fmt:'num2'},
    {key:'Days_61_90',        label:'61-90 Days',   width:110,align:'right',fmt:'num2'},
    {key:'Over_90',           label:'Over 90',      width:110,align:'right',fmt:'num2red'},
    {key:'EarliestDue',       label:'Earliest Due', width:105,fmt:'date'},
    {key:'LatestInvoice',     label:'Latest Inv',   width:105,fmt:'date'},
  ],
  statement:[
    {key:'CustomerCode', label:'Code',          width:90 },
    {key:'CustomerName', label:'Customer',      width:170},
    {key:'TxnDate',      label:'Date',          width:95, fmt:'date'},
    {key:'RefNo',        label:'Reference',     width:130},
    {key:'TxnType',      label:'Type',          width:80, fmt:'txntype'},
    {key:'Debit',        label:'Invoice (Dr)',  width:125,align:'right',fmt:'num2'},
    {key:'Credit',       label:'Receipt (Cr)',  width:125,align:'right',fmt:'num2green'},
    {key:'Description',  label:'Description',   width:260},
  ],
  receipt_history:[
    {key:'CustomerCode',      label:'Code',        width:90 },
    {key:'CustomerName',      label:'Customer',    width:160},
    {key:'ReceiptNo',         label:'Receipt No',  width:120},
    {key:'ReceiptDate',       label:'Date',        width:95, fmt:'date'},
    {key:'PaymentMode',       label:'Mode',        width:80, fmt:'paymode'},
    {key:'AccountName',       label:'Account',     width:150},
    {key:'Amount',            label:'Amount',      width:120,align:'right',fmt:'num2green'},
    {key:'ReferenceNo',       label:'Reference',   width:120},
    {key:'ChequeNo',          label:'Cheque No',   width:100},
    {key:'ReceiptType',       label:'Type',        width:80, fmt:'rcpttype'},
    {key:'Status',            label:'Status',      width:90, fmt:'rcptstatus'},
    {key:'AllocatedInvoices', label:'Invoices',    width:70, align:'right'},
    {key:'TotalAllocated',    label:'Allocated',   width:110,align:'right',fmt:'num2'},
    {key:'Notes',             label:'Notes',       width:160},
  ],
  receipt_summary_new:[
    {key:'CustomerCode',  label:'Code',           width:90 },
    {key:'CustomerName',  label:'Customer',       width:190},
    {key:'TotalReceipts', label:'Receipts',       width:85, align:'right'},
    {key:'TotalReceived', label:'Total Received', width:130,align:'right',fmt:'num2green'},
    {key:'CashTotal',     label:'Cash',           width:110,align:'right',fmt:'num2'},
    {key:'BankTotal',     label:'Bank',           width:110,align:'right',fmt:'num2'},
    {key:'AvgReceipt',    label:'Avg Receipt',    width:115,align:'right',fmt:'num2'},
    {key:'AdvanceAmt',    label:'Advance',        width:110,align:'right',fmt:'num2'},
    {key:'ClearedAmt',    label:'Cleared',        width:110,align:'right',fmt:'num2'},
    {key:'BouncedAmt',    label:'Bounced',        width:100,align:'right',fmt:'num2red'},
    {key:'FirstReceipt',  label:'First',          width:95, fmt:'date'},
    {key:'LastReceipt',   label:'Last',           width:95, fmt:'date'},
  ],
  allocation_details:[
    {key:'CustomerCode',    label:'Code',         width:90 },
    {key:'CustomerName',    label:'Customer',     width:160},
    {key:'ReceiptNo',       label:'Receipt No',   width:120},
    {key:'ReceiptDate',     label:'Receipt Date', width:95, fmt:'date'},
    {key:'PaymentMode',     label:'Mode',         width:75, fmt:'paymode'},
    {key:'ReceiptAmount',   label:'Receipt Amt',  width:115,align:'right',fmt:'num2green'},
    {key:'InvoiceNo',       label:'Invoice No',   width:120},
    {key:'InvoiceDate',     label:'Inv Date',     width:95, fmt:'date'},
    {key:'InvoiceTotal',    label:'Inv Total',    width:110,align:'right',fmt:'num2'},
    {key:'AllocatedAmount', label:'Applied',      width:110,align:'right',fmt:'num2green'},
    {key:'InvoiceBalance',  label:'Inv Balance',  width:110,align:'right',fmt:'balance'},
    {key:'InvoiceStatus',   label:'Status',       width:100,fmt:'invstatus'},
  ],
  reconciliation:[
    {key:'CustomerCode',    label:'Code',           width:90 },
    {key:'CustomerName',    label:'Customer',       width:160},
    {key:'InvoiceNo',       label:'Invoice No',     width:120},
    {key:'InvoiceDate',     label:'Date',           width:95, fmt:'date'},
    {key:'InvoiceAmount',   label:'Invoice Amt',    width:120,align:'right',fmt:'num2'},
    {key:'TotalReceived',   label:'Total Received', width:120,align:'right',fmt:'num2green'},
    {key:'BalanceDue',      label:'Balance Due',    width:115,align:'right',fmt:'balance'},
    {key:'Status',          label:'Status',         width:100,fmt:'invstatus'},
    {key:'ReceiptRefs',     label:'Receipt Nos',    width:180},
    {key:'LastReceiptDate', label:'Last Receipt',   width:105,fmt:'date'},
  ],
  invoice_receipt_summary:[
    {key:'CustomerCode',    label:'Code',           width:90 },
    {key:'CustomerName',    label:'Customer',       width:170},
    {key:'InvoiceCount',    label:'Invoices',       width:80, align:'right'},
    {key:'TotalInvoiced',   label:'Total Invoiced', width:130,align:'right',fmt:'num2'},
    {key:'ReceiptCount',    label:'Receipts',       width:80, align:'right'},
    {key:'TotalReceived',   label:'Total Received', width:130,align:'right',fmt:'num2green'},
    {key:'NetOutstanding',  label:'Outstanding',    width:125,align:'right',fmt:'balance'},
    {key:'PaidInvoices',    label:'Paid Inv',       width:75, align:'right'},
    {key:'PartialInvoices', label:'Partial',        width:70, align:'right'},
    {key:'UnpaidInvoices',  label:'Unpaid',         width:70, align:'right'},
    {key:'LastInvoiceDate', label:'Last Invoice',   width:105,fmt:'date'},
    {key:'LastReceiptDate', label:'Last Receipt',   width:105,fmt:'date'},
  ],
  ledger_details:[
    {key:'CustomerCode',    label:'Code',        width:90 },
    {key:'CustomerName',    label:'Customer',    width:170},
    {key:'TransactionDate', label:'Date',        width:95, fmt:'date'},
    {key:'ReferenceNo',     label:'Reference',   width:120},
    {key:'TransactionType', label:'Type',        width:75, fmt:'txntype'},
    {key:'DebitAmount',     label:'Debit',       width:110,align:'right',fmt:'num2'},
    {key:'CreditAmount',    label:'Credit',      width:110,align:'right',fmt:'num2green'},
    {key:'RunningBalance',  label:'Balance',     width:120,align:'right',fmt:'balance'},
    {key:'AgingCategory',   label:'Aging',       width:95, fmt:'aging'},
    {key:'DaysOverdue',     label:'Days OD',     width:70, align:'right',fmt:'overdue'},
    {key:'TxnStatus',       label:'Status',      width:90, fmt:'invstatus'},
    {key:'Description',     label:'Description', width:180},
  ],
  ledger_summary:[
    {key:'CustomerCode',    label:'Code',           width:90 },
    {key:'CustomerName',    label:'Customer',       width:200},
    {key:'TotalInvoices',   label:'Invoices',       width:80, align:'right'},
    {key:'DebitAmount',     label:'Total Invoiced', width:130,align:'right',fmt:'num2'},
    {key:'CreditAmount',    label:'Total Paid',     width:130,align:'right',fmt:'num2green'},
    {key:'RunningBalance',  label:'Balance',        width:130,align:'right',fmt:'balance'},
    {key:'FirstTransaction',label:'First Txn',      width:100,fmt:'date'},
    {key:'LastTransaction', label:'Last Txn',       width:100,fmt:'date'},
  ],
  aging:[
    {key:'CustomerCode',      label:'Code',         width:90 },
    {key:'CustomerName',      label:'Customer',     width:160},
    {key:'InvoiceNo',         label:'Invoice No',   width:120},
    {key:'InvoiceDate',       label:'Invoice Date', width:100,fmt:'date'},
    {key:'DueDate',           label:'Due Date',     width:100,fmt:'date'},
    {key:'InvoiceAmount',     label:'Amount',       width:110,align:'right',fmt:'num2'},
    {key:'PaidAmount',        label:'Paid',         width:100,align:'right',fmt:'num2green'},
    {key:'OutstandingAmount', label:'Outstanding',  width:120,align:'right',fmt:'balance'},
    {key:'DaysOverdue',       label:'Days OD',      width:70, align:'right',fmt:'overdue'},
    {key:'AgingCategory',     label:'Category',     width:100,fmt:'aging'},
    {key:'CurrentAmt',        label:'Current',      width:100,align:'right',fmt:'num2'},
    {key:'Days1to30',         label:'1-30 Days',    width:100,align:'right',fmt:'num2'},
    {key:'Days31to60',        label:'31-60 Days',   width:100,align:'right',fmt:'num2'},
    {key:'Days61to90',        label:'61-90 Days',   width:100,align:'right',fmt:'num2'},
    {key:'Over90Days',        label:'Over 90',      width:100,align:'right',fmt:'num2red'},
    {key:'Status',            label:'Status',       width:90, fmt:'invstatus'},
  ],
  customer_list:[
    {key:'CustomerCode',       label:'Code',         width:90 },
    {key:'CustomerName',       label:'Customer Name',width:180},
    {key:'CustomerType',       label:'Type',         width:80 },
    {key:'Phone',              label:'Phone',        width:120},
    {key:'Email',              label:'Email',        width:170},
    {key:'CreditLimit',        label:'Credit Limit', width:110,align:'right',fmt:'num2'},
    {key:'TotalInvoiced',      label:'Invoiced',     width:120,align:'right',fmt:'num2'},
    {key:'TotalPaid',          label:'Paid',         width:110,align:'right',fmt:'num2green'},
    {key:'OutstandingBalance', label:'Outstanding',  width:120,align:'right',fmt:'balance'},
    {key:'TotalInvoices',      label:'# Invoices',   width:80, align:'right'},
    {key:'LastInvoiceDate',    label:'Last Invoice', width:110,fmt:'date'},
    {key:'Address',            label:'Address',      width:200},
  ],
  invoice_details:[
    {key:'CustomerCode',      label:'Code',        width:90 },
    {key:'CustomerName',      label:'Customer',    width:160},
    {key:'InvoiceNo',         label:'Invoice No',  width:120},
    {key:'InvoiceDate',       label:'Date',        width:95, fmt:'date'},
    {key:'DueDate',           label:'Due Date',    width:95, fmt:'date'},
    {key:'JobReference',      label:'Job Ref',     width:110},
    {key:'SubTotal',          label:'Sub Total',   width:110,align:'right',fmt:'num2'},
    {key:'TaxAmount',         label:'Tax',         width:90, align:'right',fmt:'num2'},
    {key:'InvoiceAmount',     label:'Total',       width:120,align:'right',fmt:'num2'},
    {key:'PaidAmount',        label:'Paid',        width:110,align:'right',fmt:'num2green'},
    {key:'OutstandingAmount', label:'Outstanding', width:120,align:'right',fmt:'balance'},
    {key:'Status',            label:'Status',      width:95, fmt:'invstatus'},
    {key:'AgingCategory',     label:'Aging',       width:95, fmt:'aging'},
  ],
  invoice_summary:[
    {key:'CustomerCode',    label:'Code',           width:90 },
    {key:'CustomerName',    label:'Customer',       width:170},
    {key:'TotalInvoices',   label:'Invoices',       width:80, align:'right'},
    {key:'TotalSubTotal',   label:'Sub Total',      width:120,align:'right',fmt:'num2'},
    {key:'TotalTax',        label:'Tax',            width:100,align:'right',fmt:'num2'},
    {key:'TotalInvoiced',   label:'Total Invoiced', width:130,align:'right',fmt:'num2'},
    {key:'TotalPaid',       label:'Total Paid',     width:120,align:'right',fmt:'num2green'},
    {key:'TotalOutstanding',label:'Outstanding',    width:120,align:'right',fmt:'balance'},
    {key:'AvgInvoiceAmount',label:'Avg Invoice',    width:110,align:'right',fmt:'num2'},
    {key:'PaidCount',       label:'Paid #',         width:65, align:'right'},
    {key:'UnpaidCount',     label:'Unpaid #',       width:70, align:'right'},
    {key:'FirstInvoiceDate',label:'First',          width:95, fmt:'date'},
    {key:'LastInvoiceDate', label:'Last',           width:95, fmt:'date'},
  ],
};

const formatDate = v => {
  if (!v || v==='—') return v||'';
  const d = new Date(v); if (isNaN(d)) return v;
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};
const fmt2 = v => (v!=null&&v!=='') ? parseFloat(v).toFixed(2) : '';

const AGING_COLORS = {
  'Current':     { bg:'#f0fdf4',color:'#15803d' },
  '1-30 Days':   { bg:'#eff6ff',color:'#1d4ed8' },
  '31-60 Days':  { bg:'#fefce8',color:'#a16207' },
  '61-90 Days':  { bg:'#fff7ed',color:'#c2410c' },
  'Over 90 Days':{ bg:'#fef2f2',color:'#dc2626' },
};
const INV_STATUS_COLORS  = { 'Paid':'#15803d','Partially Paid':'#d97706','Overdue':'#dc2626','Sent':'#1d4ed8','Draft':'#6b7280','Cancelled':'#9ca3af' };
const RCPT_STATUS_COLORS = { 'Received':'#1d4ed8','Cleared':'#15803d','Bounced':'#dc2626','Cancelled':'#9ca3af' };

function CellValue({ col, value }) {
  const v = value??'';
  switch (col.fmt) {
    case 'date':      return <span>{formatDate(v)}</span>;
    case 'num2':      return <span style={{ fontFamily:'monospace' }}>{fmt2(v)}</span>;
    case 'num2green': { const n=parseFloat(v)||0; return <span style={{ fontFamily:'monospace',color:n>0?'#15803d':undefined }}>{fmt2(v)}</span>; }
    case 'num2red':   { const n=parseFloat(v)||0; return <span style={{ fontFamily:'monospace',color:n>0?'#dc2626':undefined,fontWeight:n>0?700:undefined }}>{fmt2(v)}</span>; }
    case 'balance':   { const n=parseFloat(v)||0; return <span style={{ fontFamily:'monospace',fontWeight:700,color:n>0?'#dc2626':n<0?'#15803d':undefined }}>{fmt2(v)}</span>; }
    case 'txntype':   { const isR=String(v).toLowerCase()==='receipt'; return <span style={{ fontWeight:600,color:isR?'#15803d':'#1d4ed8' }}>{v}</span>; }
    case 'aging':     { const s=AGING_COLORS[v]||{}; return v?<span style={{ padding:'2px 7px',borderRadius:10,fontSize:'0.75rem',fontWeight:600,background:s.bg,color:s.color }}>{v}</span>:<span>{v}</span>; }
    case 'overdue':   { const n=parseInt(v)||0; return <span style={{ fontFamily:'monospace',fontWeight:n>0?700:undefined,color:n>90?'#dc2626':n>60?'#ea580c':n>30?'#d97706':n>0?'#ca8a04':undefined }}>{v}</span>; }
    case 'invstatus': { return <span style={{ fontWeight:600,color:INV_STATUS_COLORS[v]||'#374151' }}>{v}</span>; }
    case 'rcptstatus':{ return <span style={{ fontWeight:600,color:RCPT_STATUS_COLORS[v]||'#374151' }}>{v}</span>; }
    case 'rcpttype':  { const isAdv=String(v)==='Advance'; return <span style={{ display:'inline-block',padding:'2px 7px',borderRadius:10,fontSize:'0.75rem',fontWeight:700,background:isAdv?'#fffbeb':'#eff6ff',color:isAdv?'#92400e':'#1d4ed8',border:`1px solid ${isAdv?'#fbbf24':'#bfdbfe'}` }}>{v||'Invoice'}</span>; }
    case 'paymode':   { const isBank=String(v)==='Bank'; return <span style={{ display:'inline-block',padding:'2px 8px',borderRadius:10,fontSize:'0.75rem',fontWeight:700,background:isBank?'#eff6ff':'#f0fdf4',color:isBank?'#1d4ed8':'#15803d',border:`1px solid ${isBank?'#bfdbfe':'#86efac'}` }}>{isBank?'🏦 Bank':'💵 Cash'}</span>; }
    default:          return <span>{String(v)}</span>;
  }
}

const StatBadge = ({ label, value, color }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[0.7rem] text-gray-500 font-semibold uppercase">{label}</span>
    <span className="text-[1rem] font-extrabold font-mono" style={{ color }}>{value}</span>
  </div>
);

const CustomerReports = () => {
  const today      = new Date().toISOString().slice(0,10);
  const monthStart = today.slice(0,8)+'01';

  const [reportType, setReportType] = useState('outstanding');
  const [startDate,  setStartDate]  = useState(monthStart);
  const [endDate,    setEndDate]    = useState(today);
  const [customerID, setCustomerID] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [customers, setCustomers] = useState([]);
  const [rows,      setRows]      = useState([]);
  const [summary,   setSummary]   = useState({});
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [sortKey,   setSortKey]   = useState('');
  const [sortDir,   setSortDir]   = useState('asc');

  useEffect(() => {
    apiClient.get('/CustomerReports.php?action=dropdowns')
      .then(res => { if (res.data.success) setCustomers(res.data.data.customers||[]); })
      .catch(err => console.error('Dropdown error:', err));
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true); setError(''); setHasLoaded(false); setSortKey('');
    const params = new URLSearchParams({ report_type:reportType, start_date:startDate, end_date:endDate, customer_id:customerID });
    try {
      const res = await apiClient.get(`/CustomerReports.php?${params}`);
      if (res.data.success) { setRows(res.data.data.rows||[]); setSummary(res.data.data.summary||{}); setHasLoaded(true); }
      else setError(res.data.message||'Failed to load report');
    } catch (err) { setError(err.response?.data?.message||err.message||'Error loading report'); }
    finally { setLoading(false); }
  }, [reportType, startDate, endDate, customerID]);

  const displayRows = (() => {
    let r = rows;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      r = r.filter(row => Object.values(row).some(v => String(v??'').toLowerCase().includes(q)));
    }
    if (sortKey) {
      r = [...r].sort((a,b) => {
        const av=a[sortKey]??'', bv=b[sortKey]??'';
        const an=parseFloat(av), bn=parseFloat(bv);
        const cmp = !isNaN(an)&&!isNaN(bn) ? an-bn : String(av).localeCompare(String(bv));
        return sortDir==='asc' ? cmp : -cmp;
      });
    }
    return r;
  })();

  const handleSort = key => {
    if (sortKey===key) setSortDir(d=>d==='asc'?'desc':'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const exportCSV = () => {
    const cols = COLUMNS[reportType]||[];
    const header = cols.map(c=>c.label).join(',');
    const body   = displayRows.map(row=>cols.map(c=>`"${String(row[c.key]??'').replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([header+'\n'+body],{type:'text/csv'}));
    a.download = `${reportType}_${startDate}_${endDate}.csv`; a.click();
  };

  const handlePrint = () => {
    const cols = COLUMNS[reportType]||[];
    const rt   = REPORT_TYPES.find(r=>r.key===reportType);
    const win  = window.open('','_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${rt?.label}</title><style>body{font-family:Arial,sans-serif;font-size:11px;color:#1f2937}h2{margin:0 0 4px;font-size:15px}p.sub{margin:0 0 10px;color:#555;font-size:10px}table{border-collapse:collapse;width:100%}th{background:#667eea;color:#fff;padding:5px 7px;font-size:10px;text-align:left}td{padding:4px 7px;border-bottom:1px solid #e5e7eb}tr:nth-child(even) td{background:#f9fafb}@media print{@page{margin:10mm}}</style></head><body><h2>Customer Reports — ${rt?.label}</h2><p class="sub">Period: ${startDate} to ${endDate} | Records: ${displayRows.length}</p><table><thead><tr>${cols.map(c=>`<th>${c.label}</th>`).join('')}</tr></thead><tbody>${displayRows.map(row=>`<tr>${cols.map(c=>{let v=row[c.key]??'';if(c.fmt==='date')v=formatDate(v);else if(c.fmt&&c.fmt.startsWith('num'))v=fmt2(v);const align=c.align==='right'?' style="text-align:right"':'';return`<td${align}>${v}</td>`;}).join('')}</tr>`).join('')}</tbody></table><script>window.onload=()=>{window.print();}</script></body></html>`);
    win.document.close();
  };

  const cols = COLUMNS[reportType]||[];
  const currentRpt = REPORT_TYPES.find(r=>r.key===reportType);

  const inpCls = 'w-full px-2.5 py-2 border border-gray-300 rounded-md text-[0.85rem] bg-white box-border outline-none focus:border-violet-400 transition-colors duration-150';
  const lblCls = 'block text-[0.78rem] font-semibold text-gray-700 mb-1';

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        .cr-row:hover td { background:#f5f3ff !important; }
      `}</style>

      {/* Header */}
      <div className="flex items-center gap-5 px-7 py-5 flex-shrink-0 border-b-4"
        style={{ background:`linear-gradient(135deg,${VIOLET} 0%,${VIOLET_DK} 100%)`, borderBottomColor:'#5a67d8', boxShadow:'0 4px 12px rgba(0,0,0,0.15)' }}>
        <div className="w-[52px] h-[52px] bg-white/25 rounded-[14px] flex items-center justify-center flex-shrink-0">
          <Users size={26} color="#fff" />
        </div>
        <div>
          <div className="text-[1.6rem] font-extrabold text-white">Customer Reports</div>
          <div className="text-[0.85rem] mt-0.5" style={{ color:'rgba(255,255,255,0.8)' }}>
            {currentRpt?.label}{hasLoaded&&` · ${displayRows.length} records`}
          </div>
        </div>
      </div>

      {/* Grouped tabs */}
      <div className="bg-gray-100 border-b border-gray-200 flex-shrink-0 overflow-x-auto">
        {REPORT_GROUPS.map(group => (
          <div key={group.label} className="flex items-center gap-1 px-3 pt-1.5 flex-nowrap">
            <span className="text-[0.7rem] font-bold text-gray-400 whitespace-nowrap min-w-[100px] pr-1.5">{group.label}</span>
            {group.types.map(rt => {
              const active = rt.key === reportType;
              const Icon   = rt.icon;
              return (
                <button key={rt.key}
                  onClick={() => { setReportType(rt.key); setHasLoaded(false); setSortKey(''); }}
                  className={`flex items-center gap-1 px-3 py-1.5 border-0 cursor-pointer whitespace-nowrap text-[0.79rem] transition-all duration-100 rounded-t-lg ${active ? 'font-bold bg-white -mb-px border-b-2' : 'font-medium bg-transparent text-gray-500 border-b-2 border-transparent hover:text-violet-600'}`}
                  style={active ? { color: VIOLET, borderBottomColor: VIOLET } : {}}>
                  <Icon size={12} />{rt.label}
                </button>
              );
            })}
          </div>
        ))}
        <div className="h-2 bg-gray-100" />
      </div>

      {/* Filters */}
      <div className="px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="grid gap-2.5 items-end" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))' }}>
          <div><label className={lblCls}>From Date</label><input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} className={inpCls} /></div>
          <div><label className={lblCls}>To Date</label><input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} className={inpCls} /></div>
          <div><label className={lblCls}>Customer</label>
            <select value={customerID} onChange={e=>setCustomerID(e.target.value)} className={`${inpCls} cursor-pointer`}>
              <option value="">All Customers</option>
              {customers.map(c=><option key={c.customerid} value={c.customerid}>{c.customercode} – {c.customername}</option>)}
            </select>
          </div>
          <div>
            <label className={lblCls}>&nbsp;</label>
            <button onClick={loadReport} disabled={loading}
              className="w-full flex items-center justify-center gap-1.5 px-3.5 py-2.5 border-0 rounded-md text-white font-bold text-[0.9rem] cursor-pointer disabled:cursor-not-allowed transition-all duration-150"
              style={{ background:loading?'#9ca3af':`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
              {loading
                ? <><span style={{ width:16,height:16,border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }} />Loading…</>
                : <><RefreshCw size={14} />Load Report</>}
            </button>
          </div>
        </div>
      </div>

      {/* Summary bar */}
      {hasLoaded && (
        <div className="flex items-center gap-6 flex-wrap px-5 py-2.5 bg-violet-50 border-b border-violet-200 flex-shrink-0">
          <StatBadge label="Records"       value={displayRows.length}                                        color={VIOLET} />
          {summary.total_debit  > 0 && <StatBadge label="Total Invoiced" value={parseFloat(summary.total_debit).toFixed(2)}  color="#dc2626" />}
          {summary.total_credit > 0 && <StatBadge label="Total Received" value={parseFloat(summary.total_credit).toFixed(2)} color="#15803d" />}
          {summary.total_balance!=null && summary.total_balance!==0 && (
            <StatBadge label="Outstanding" value={parseFloat(summary.total_balance).toFixed(2)} color={parseFloat(summary.total_balance)>=0?'#dc2626':'#15803d'} />
          )}
          <div className="ml-auto flex gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 border border-violet-200 rounded-lg bg-white">
              <Search size={14} className="text-gray-400" />
              <input type="text" placeholder="Search…" value={searchTerm} onChange={e=>setSearchTerm(e.target.value)}
                className="border-0 outline-none w-[140px] text-[0.85rem] bg-transparent" />
            </div>
            <button onClick={exportCSV} className="flex items-center gap-1.5 px-3 py-1.5 border border-violet-200 rounded-lg bg-white font-semibold text-[0.82rem] cursor-pointer hover:bg-violet-50 transition-colors duration-100" style={{ color:VIOLET }}>
              <Download size={15} /> CSV
            </button>
            <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 border border-violet-200 rounded-lg bg-white font-semibold text-[0.82rem] cursor-pointer hover:bg-violet-50 transition-colors duration-100" style={{ color:VIOLET }}>
              <Printer size={15} /> Print
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mx-5 mt-3 px-3.5 py-2.5 rounded-lg bg-red-50 border border-red-300 text-red-700 text-[0.9rem] flex-shrink-0">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Table area */}
      <div className="flex-1 overflow-auto">
        {!hasLoaded && !loading && (
          <div className="text-center py-16 text-gray-400">
            <Users size={48} className="mx-auto mb-3 opacity-35" />
            <p className="m-0">Select filters and click <strong>Load Report</strong></p>
          </div>
        )}
        {loading && (
          <div className="text-center py-16" style={{ color:VIOLET }}>
            <span style={{ width:32,height:32,border:'3px solid rgba(102,126,234,0.2)',borderTopColor:VIOLET,borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block',marginBottom:12 }} />
            <p className="m-0">Loading report…</p>
          </div>
        )}
        {hasLoaded && !loading && (
          displayRows.length===0 ? (
            <div className="text-center py-16 text-gray-400">
              <FileText size={40} className="mx-auto mb-2.5 opacity-40" />
              <p className="m-0">No data found for the selected filters.</p>
            </div>
          ) : (
            <table className="w-full border-collapse text-[0.82rem]" style={{ minWidth:800 }}>
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-100">
                  {cols.map(c => (
                    <th key={c.key} onClick={() => handleSort(c.key)}
                      className="py-2.5 px-2.5 border-b-2 border-r border-gray-200 font-bold text-[0.78rem] text-gray-700 cursor-pointer whitespace-nowrap select-none"
                      style={{ textAlign:c.align||'left', minWidth:c.width }}>
                      <span className="inline-flex items-center gap-0.5">
                        {c.label}
                        {sortKey===c.key
                          ? sortDir==='asc' ? <ChevronUp size={12} color={VIOLET}/> : <ChevronDown size={12} color={VIOLET}/>
                          : <ChevronUp size={12} color="#d1d5db"/>}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row,i) => (
                  <tr key={i} className="cr-row" style={{ background:i%2===0?'#fff':'#f9fafb' }}>
                    {cols.map(c => (
                      <td key={c.key} className="py-1.5 px-2.5 border-b border-r border-gray-100"
                        style={{ textAlign:c.align||'left', whiteSpace:c.width>140?'normal':'nowrap' }}>
                        <CellValue col={c} value={row[c.key]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {displayRows.length>1 && (
                <tfoot>
                  <tr className="bg-violet-50 font-bold">
                    {cols.map((c,ci) => {
                      if (ci===0) return <td key={c.key} className="px-2.5 py-2 border-t-2 border-violet-200 text-[0.8rem] text-gray-800">TOTAL ({displayRows.length})</td>;
                      if (c.fmt&&(c.fmt.startsWith('num')||c.fmt==='balance')) {
                        const sum = displayRows.reduce((s,r)=>s+(parseFloat(r[c.key])||0),0);
                        return <td key={c.key} className="px-2.5 py-2 border-t-2 border-violet-200 text-[0.8rem] text-gray-800 text-right font-mono">{fmt2(sum)}</td>;
                      }
                      if (c.align==='right'&&!c.fmt) {
                        const sum = displayRows.reduce((s,r)=>s+(parseInt(r[c.key])||0),0);
                        return <td key={c.key} className="px-2.5 py-2 border-t-2 border-violet-200 text-[0.8rem] text-gray-800 text-right font-mono">{sum}</td>;
                      }
                      return <td key={c.key} className="px-2.5 py-2 border-t-2 border-violet-200" />;
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          )
        )}
      </div>
    </div>
  );
};

export default CustomerReports;